import {
	Button,
	Checkbox,
	Chip,
	H2,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { useGetReceptionSupportingDocumentsAnalysis } from "../../../../api/queries/constructionSiteAnalysis.queries";
import { useGetConstructionSiteReceptionParticipants } from "../../../../api/queries/constructionSiteSubmission.queries";
import { PreviewFileLink } from "../../../../component/common/files/PreviewFileLink";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import {
	type ParticipantToConstructionSiteReception,
	type NonCompliantDocument,
	type DocumentToProcess,
	PAGES,
} from "@dune/types";
import { FC } from "react";
import { useLinkFileToConstructionSiteReception } from "../../../../api/mutations/constructionSiteReception.mutations";
import { useNavigate } from "react-router-dom";

type Props = { onPrev: () => void; onNext: () => void };

export const ReceptionAnalysisReserves = ({ onPrev, onNext }: Props) => {
	const navigate = useNavigate();

	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();
	const { data, isLoading, error } = useGetReceptionSupportingDocumentsAnalysis(
		Number(constructionSiteId),
	);
	const { data: receptionParticipants } =
		useGetConstructionSiteReceptionParticipants(Number(constructionSiteId));
	const linkFileMutation = useLinkFileToConstructionSiteReception(
		Number(constructionSiteId),
	);

	const handleClose = async () => {
		navigate(PAGES.PROJECTS.show.withParams(constructionSiteId));
	};

	const nonCompliant = (data?.nonCompliantDocuments ?? []).filter(
		(doc) => doc.documentType === "PV_RECEPTION",
	);

	const participantsWithNonCompliantPV = new Set<string>();
	for (const doc of nonCompliant) {
		if (doc.participant) {
			participantsWithNonCompliantPV.add(doc.participant.name);
		}
	}

	const leveReservesFromProcess = (data?.documentsToProcess ?? []).filter(
		(doc) => doc.documentType === "LEVEE_RESERVES_PV_RECEPTION",
	);
	const leveReservesFromNonCompliant = (
		data?.nonCompliantDocuments ?? []
	).filter((doc) => doc.documentType === "LEVEE_RESERVES_PV_RECEPTION");
	const leveReserves = [
		...leveReservesFromProcess,
		...leveReservesFromNonCompliant,
	];

	const participantsWithLeveReserves = new Set<string>();
	for (const doc of leveReserves) {
		if (doc.participant) {
			participantsWithLeveReserves.add(doc.participant.name);
		}
	}

	const getPVFiles = (participantId: number) => {
		const participant = (receptionParticipants || [])
			.flatMap((p) => p.participant)
			.find((p) => p.id === participantId);

		if (!participant) return [];

		return (participant.filesOnConstructionSiteReception || []).filter(
			(f) => f.type === "PV_RECEPTION",
		);
	};

	const hasLeveReservesFile = (participantId: number): boolean => {
		const participant = (receptionParticipants || [])
			.flatMap((p) => p.participant)
			.find((p) => p.id === participantId);

		if (!participant) return false;

		return (participant.filesOnConstructionSiteReception || []).some(
			(f) => f.type === "LEVEE_RESERVES_PV_RECEPTION",
		);
	};

	const handleToggleLiftingReserves = async (
		pvFileId: string,
		participantId: number,
		newState: boolean,
	) => {
		await linkFileMutation.mutateAsync({
			fileId: pvFileId,
			type: "PV_RECEPTION",
			participantId,
			requiresLiftingReserves: newState,
		});
	};

	if (isLoading) return <div>Chargement des réserves...</div>;
	if (error) return <div>Erreur lors du chargement des réserves.</div>;

	return (
		<div className="flex flex-col gap-6 p-6">
			<H2>Réserves</H2>

			<div className="flex flex-col gap-2">
				<div className="text-[#344054] text-sm">Non conforme</div>
				{nonCompliant.length === 0 ? (
					<div className="bg-white rounded-lg border p-6 text-sm text-gray-500">
						Aucun document non conforme
					</div>
				) : (
					<NonCompliantTable
						participantsWithNonCompliantPV={participantsWithNonCompliantPV}
						nonCompliant={nonCompliant}
						receptionParticipants={receptionParticipants || []}
						getPVFiles={getPVFiles}
						hasLeveReservesFile={hasLeveReservesFile}
						handleToggleLiftingReserves={handleToggleLiftingReserves}
					/>
				)}
			</div>

			{participantsWithLeveReserves.size > 0 && (
				<div className="flex flex-col gap-2">
					<div className="text-[#344054] text-sm">Levées de réserves</div>
					<LeveReservesTable
						participantsWithLeveReserves={participantsWithLeveReserves}
						leveReserves={leveReserves}
						receptionParticipants={receptionParticipants || []}
					/>
				</div>
			)}

			<div className="flex justify-between mt-2">
				<Button variant="outline" onClick={onPrev}>
					Précédent
				</Button>
				<div className="flex gap-2">
					<Button variant="outline" onClick={handleClose}>
						Enregistrer et fermer
					</Button>
					<Button onClick={onNext}>Suivant</Button>
				</div>
			</div>
		</div>
	);
};

export default ReceptionAnalysisReserves;

type ReceptionParticipant = ParticipantToConstructionSiteReception;
type PVFileType =
	ReceptionParticipant["participant"]["filesOnConstructionSiteReception"][number];

interface NonCompliantTableProps {
	participantsWithNonCompliantPV: Set<string>;
	nonCompliant: NonCompliantDocument[];
	receptionParticipants: ReceptionParticipant[];
	getPVFiles: (participantId: number) => PVFileType[];
	hasLeveReservesFile: (participantId: number) => boolean;
	handleToggleLiftingReserves: (
		fileId: string,
		participantId: number,
		state: boolean,
	) => Promise<void>;
}

const NonCompliantTable: FC<NonCompliantTableProps> = ({
	participantsWithNonCompliantPV,
	nonCompliant,
	receptionParticipants,
	getPVFiles,
	hasLeveReservesFile,
	handleToggleLiftingReserves,
}) => (
	<div className="bg-white rounded-lg border">
		<Table className="table-fixed w-full">
			<TableHeader>
				<TableRow>
					<TableHead>Participant</TableHead>
					<TableHead>Statut</TableHead>
					<TableHead>Type</TableHead>
					<TableHead>Documents</TableHead>
					<TableHead>Commentaire</TableHead>
					<TableHead className="text-center">Demander justificatif</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{Array.from(participantsWithNonCompliantPV).map((participantName) => {
					const receptionData = (receptionParticipants || []).find(
						(p) =>
							p.participant?.society?.denomination === participantName ||
							p.participant?.foreignSociety?.denomination === participantName,
					);

					if (!receptionData) return null;

					const participant = receptionData.participant;
					const pvFiles = getPVFiles(participant.id);
					const hasLeveReserves = hasLeveReservesFile(participant.id);
					const requiresLiftingReserves = receptionData.requiresLiftingReserves;

					const nonCompliantForParticipant = nonCompliant.filter(
						(doc) => doc.participant?.name === participantName,
					);

					return (
						<TableRow key={participantName}>
							<TableCell className="align-middle">
								<div className="flex flex-col gap-2">
									<span>{participantName}</span>
								</div>
							</TableCell>
							<TableCell className="align-middle">
								<div className="flex flex-col gap-2">
									{pvFiles.map((pvFile) => {
										const fileObj = pvFile.File;
										const fileId = fileObj.id;
										const humanStatus = fileObj.humanProcessingStatus;
										return (
											<div key={fileId}>
												<Chip
													label={
														humanStatus === "NON_COMPLIANT"
															? "Non conforme"
															: humanStatus
													}
													color={
														humanStatus === "NON_COMPLIANT" ? "red" : "orange"
													}
												/>
											</div>
										);
									})}
								</div>
							</TableCell>
							<TableCell className="align-middle">
								<div className="flex flex-col gap-2">
									{pvFiles.map((pvFile) => {
										const fileId = pvFile.File.id;
										return <span key={fileId}>{pvFile.type}</span>;
									})}
								</div>
							</TableCell>
							<TableCell className="align-middle">
								<div className="flex flex-col gap-2">
									{pvFiles.map((pvFile) => {
										const fileId = pvFile.File.id;
										const fileObj = pvFile.File;
										return fileObj.id ? (
											<PreviewFileLink
												key={fileId}
												file={{
													id: fileId,
													name: fileObj.name,
												}}
											/>
										) : (
											<span key={fileId}>{fileObj.name}</span>
										);
									})}
								</div>
							</TableCell>
							<TableCell className="align-middle">
								<div className="flex flex-col gap-2">
									{nonCompliantForParticipant.length > 0
										? nonCompliantForParticipant.map((doc) => (
												<span
													key={`${participantName}-comment-${doc.documentType}`}
												>
													{doc.comment ?? "—"}
												</span>
											))
										: [<span key="empty">—</span>]}
								</div>
							</TableCell>
							<TableCell className="align-middle">
								<div className="flex flex-col gap-2 items-center">
									<Checkbox
										checked={requiresLiftingReserves}
										disabled={hasLeveReserves}
										onCheckedChange={async () => {
											if (pvFiles.length > 0) {
												await handleToggleLiftingReserves(
													pvFiles[0].File.id,
													participant.id,
													!requiresLiftingReserves,
												);
											}
										}}
									/>
								</div>
							</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	</div>
);

interface LeveReservesTableProps {
	participantsWithLeveReserves: Set<string>;
	leveReserves: (DocumentToProcess | NonCompliantDocument)[];
	receptionParticipants: ReceptionParticipant[];
}

const LeveReservesTable: FC<LeveReservesTableProps> = ({
	participantsWithLeveReserves,
	leveReserves,
	receptionParticipants,
}) => (
	<div className="bg-white rounded-lg border">
		<Table className="table-fixed w-full">
			<TableHeader>
				<TableRow>
					<TableHead className="w-1/6">Participant</TableHead>
					<TableHead className="w-1/6">Statut</TableHead>
					<TableHead className="w-1/6">Type</TableHead>
					<TableHead className="w-1/6">Documents</TableHead>
					<TableHead className="w-1/3">Commentaire</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{Array.from(participantsWithLeveReserves).map((participantName) => {
					const receptionData = (receptionParticipants || []).find(
						(p) =>
							p.participant?.society?.denomination === participantName ||
							p.participant?.foreignSociety?.denomination === participantName,
					);

					if (!receptionData) return null;

					const participant = receptionData.participant;
					if (!participant) return null;

					const leveReservesFiles = (
						participant.filesOnConstructionSiteReception || []
					).filter((f) => f.type === "LEVEE_RESERVES_PV_RECEPTION");

					const leveReservesForParticipant = leveReserves.filter(
						(doc) => doc.participant?.name === participantName,
					);

					return (
						<TableRow key={participantName}>
							<TableCell className="w-1/6 align-middle">
								<div className="flex flex-col gap-2">
									<span>{participantName}</span>
								</div>
							</TableCell>
							<TableCell className="w-1/6 align-middle">
								<div className="flex flex-col gap-2">
									{leveReservesFiles.map((file) => {
										let label: string;
										const fileObj = file.File;
										const status = fileObj.humanProcessingStatus;
										if (status === "NON_COMPLIANT") {
											label = "Non conforme";
										} else if (status === "TO_BE_PROCESSED") {
											label = "À traiter";
										} else {
											label = status;
										}
										const color = status === "NON_COMPLIANT" ? "red" : "orange";
										return (
											<div key={fileObj.id}>
												<Chip label={label} color={color} />
											</div>
										);
									})}
								</div>
							</TableCell>
							<TableCell className="w-1/6 align-middle">
								<div className="flex flex-col gap-2">
									{leveReservesFiles.map((file) => (
										<span key={file.File.id}>{file.type}</span>
									))}
								</div>
							</TableCell>
							<TableCell className="w-1/6 align-middle">
								<div className="flex flex-col gap-2">
									{leveReservesFiles.map((file) => {
										const fileObj = file.File;
										return fileObj.id ? (
											<PreviewFileLink
												key={fileObj.id}
												file={{
													id: fileObj.id,
													name: fileObj.name,
												}}
											/>
										) : (
											<span key={fileObj.id}>{fileObj.name}</span>
										);
									})}
								</div>
							</TableCell>
							<TableCell className="w-1/3 align-middle">
								<div className="flex flex-col gap-2">
									{leveReservesForParticipant.length > 0
										? leveReservesForParticipant.map((doc) => {
												const comment = "comment" in doc ? doc.comment : null;
												return (
													<span
														key={`${participantName}-comment-${doc.documentType}`}
													>
														{comment ?? "—"}
													</span>
												);
											})
										: [<span key="empty">—</span>]}
								</div>
							</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	</div>
);
